import { useRef } from 'react';
import { throttle } from 'lodash';
import { Button, makeStyles, TextField } from '@material-ui/core';
import { CustomizedTypography, Modal } from '@components/Customized';
import {
  CapaErrorCode,
  CapaPurchaseOrderStatus,
  useFetchPurchaseOrdersLazyQuery
} from '@generated/graphql';
import { processEvents } from '@utils/customEvent';
import { PaymentModalProps } from './types';
import usePaymentModal from './usePaymentModal';

interface CancelRequestModalProps extends PaymentModalProps {
  rfqId?: string;
  quotationPartnerId?: string;
  purchaseOrderId?: string;
  amount?: number;
}

const CancelRequestModal: React.FunctionComponent<CancelRequestModalProps> = ({
  rfqId = '',
  quotationPartnerId = '',
  purchaseOrderId,
  openButton,
  amount
}) => {
  const {
    open,
    onOpen,
    onClose,
    loading,
    setLoading,
    enqueueSnackbar
  } = usePaymentModal();
  const reason = useRef('');
  const textCounter = useRef<HTMLSpanElement>(null);
  const submitButton = useRef<HTMLButtonElement>(null);
  const [fetchPurchaseOrders] = useFetchPurchaseOrdersLazyQuery();
  const classes = useStyles();

  const onSubmit = throttle(async () => {
    if (!purchaseOrderId || !amount) return;

    if (!reason.current) {
      throw new Error('취소사유를 입력해주세요.');
    }

    setLoading(true);
    try {
      const result = await processEvents.결제취소({
        rfqId,
        quotationPartnerId,
        purchaseOrderId,
        requestRefund: {
          amount,
          reason: reason.current
        }
      });

      const isSuccess = result.result === CapaErrorCode.Success;

      if (!isSuccess) throw new Error(result.errorMessage);

      enqueueSnackbar('취소 요청이 파트너에게 발송되었습니다.');
      onClose();
    } catch (e) {
      if ((e as Error).message) {
        enqueueSnackbar((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, 1000);

  const onClickCancelPayment = async () => {
    try {
      if (!rfqId) {
        throw new Error('견적요청을 찾을 수 없습니다.');
      }

      if (!quotationPartnerId) {
        throw new Error('파트너를 찾을 수 없습니다.');
      }

      const { data, error } = await fetchPurchaseOrders({
        variables: {
          input: {
            quotationPartnerId,
            rfqId
          }
        },
        fetchPolicy: 'no-cache'
      });

      if (error) {
        throw new Error(
          '발주서 상태 확인에 실패했습니다. 잠시 후 다시 시도해주세요.'
        );
      }

      const purchaseOrders = data?.fetchPurchaseOrders?.data ?? [];
      const isNoOrder = purchaseOrders.length === 0;

      if (isNoOrder) {
        throw new Error('발주서를 찾을 수 없습니다.');
      }

      const latestOne = purchaseOrders[0];
      const isProcessStarted =
        latestOne.status === CapaPurchaseOrderStatus.Processing;
      const isRejectedOrder =
        latestOne.status === CapaPurchaseOrderStatus.RejectOrderByPartner;

      if (isProcessStarted) {
        throw new Error(
          '파트너가 이미 제조를 시작해서 결제 취소를 할 수 없습니다. 파트너와 환불 협의 후 환불이 가능합니다.'
        );
      }

      if (isRejectedOrder) {
        throw new Error(
          '파트너가 거절한 발주서입니다. 파트너와 협의 후 새로운 발주서를 발행해주세요.'
        );
      }
    } catch (e) {
      const error: Error = e as Error;
      return enqueueSnackbar(error.message);
    }

    onOpen();
  };

  return (
    <>
      {openButton(onClickCancelPayment)}
      <Modal open={open} onClose={onClose}>
        <Modal.CloseButton onClose={onClose} />

        <Modal.Title>결제취소 요청</Modal.Title>

        <Modal.ContentSection>
          <Modal.Text
            className={classes.text}
            variant="body1"
            color="textPrimary"
            fontWeight="500"
          >
            결제취소 사유 입력
            <span ref={textCounter}>(0/500)</span>
          </Modal.Text>
          <TextField
            onChange={({ currentTarget }) => {
              const input = currentTarget?.value ?? '';
              const trimmedInput = input.trim();
              const hasReason = trimmedInput.length > 0;

              if (!submitButton.current) return;

              if (hasReason) {
                submitButton.current.classList.remove('Mui-disabled');
                submitButton.current.disabled = false;
              } else {
                submitButton.current.classList.add('Mui-disabled');
                submitButton.current.disabled = true;
              }

              if (trimmedInput.length > 500) return;
              reason.current = trimmedInput;
              if (!textCounter.current) return;
              textCounter.current.innerText = `${trimmedInput.length}/500`;
            }}
            onBlur={({ currentTarget: { value } }) => {
              const input = value ?? '';
              const trimmedInput = input.trim();

              if (trimmedInput.length > 500) {
                reason.current = trimmedInput.slice(500);
              }
            }}
            inputProps={{ maxLength: 500 }}
            placeholder="취소 사유를 알려주세요."
            multiline
            fullWidth
            variant="outlined"
            minRows={4}
            maxRows={4}
          />
        </Modal.ContentSection>

        <Modal.ButtonSection>
          <Modal.Button color="default" onClick={onClose} disabled={loading}>
            취소
          </Modal.Button>
          <Button
            className="Mui-disabled"
            color="primary"
            variant="contained"
            ref={submitButton}
            disabled={loading}
            style={{
              borderRadius: 8
            }}
            disableElevation
            onClick={onSubmit}
          >
            <CustomizedTypography
              variant="body1"
              color="inherit"
              fontWeight="500"
            >
              완료
            </CustomizedTypography>
          </Button>
        </Modal.ButtonSection>
      </Modal>
    </>
  );
};

export default CancelRequestModal;

const useStyles = makeStyles(
  {
    text: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 12,
      '& span': {
        fontWeight: 400
      }
    }
  },
  { classNamePrefix: 'purhcase-order-reject' }
);
